import axios from 'axios';
import { ClientCurrentStageDto } from './clientsService';

// Contact DTOs (טלפון + אימייל אופציונלי)
export interface ContactDto {
  id: number;
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder: number;
}

export interface CreateContactDto {
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder?: number;
}

export interface UpdateContactDto {
  id: number;
  number: string;
  email?: string;
  label?: string;
  isPrimary: boolean;
  sortOrder: number;
}

// Types for the DTOs
export interface LeadDto {
  id: number;
  fullName: string;
  email?: string;
  contacts: ContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  departmentId?: number;
  departmentName?: string;
  notes?: string;
  statusId: number;
  statusName: string;
  statusColor: string;
  createdAt: string;
  lastUpdatedAt?: string;
  signedAt?: string; // תאריך וזמן חתימה
  contractNumber?: string; // מספר חוזה אם קיים
  benefitType: number; // סוג קצבה
  otherBenefitDescription?: string; // פירוט סוג קצבה במקרה של "אחר"
  // P8b: שיוך סוכן/ארגון מפנה (שם/מזהה לכל הצוות) + עמלות פר-תיק (null ללא CanViewClientFinance)
  agentSourceId?: number | null;
  agentSourceName?: string | null;
  organizationSourceId?: number | null;
  organizationSourceName?: string | null;
  agentSigningCommissionAmount?: number | null;
  agentApprovalCommissionPercentage?: number | null;
  organizationSigningCommissionAmount?: number | null;
  organizationApprovalCommissionPercentage?: number | null;
  // שדות משותפים לכל החוזים
  clientAddress?: string;
  clientCity?: string;
  clientIdNumber?: string;
  clientBirthDate?: string;
  isClientForeignResident?: boolean;
  // טוגל סוג מוטב — "adult" | "child" | "other"
  beneficiaryType?: string;
  // שדות הורים
  fatherName?: string;
  fatherIdNumber?: string;
  fatherAddress?: string;
  fatherCity?: string;
  fatherBirthDate?: string;
  isFatherForeignResident?: boolean;
  motherName?: string;
  motherIdNumber?: string;
  motherAddress?: string;
  motherCity?: string;
  motherBirthDate?: string;
  isMotherForeignResident?: boolean;
  // שדות בן/בת זוג
  spouseName?: string;
  spouseAddress?: string;
  spouseCity?: string;
  spouseIdNumber?: string;
  spouseBirthDate?: string;
  isSpouseForeignResident?: boolean;
  // מגיש הבקשה (החותם בפועל)
  submitterSourceType?: string;
  submitterName?: string;
  submitterRelationship?: string;
  submitterIdNumber?: string;
  submitterAddress?: string;
  submitterCity?: string;
  // נתוני הלקוח עבור ליד שחתם — מגיעים מהשרת רק לנציגי מכירות וכשהפרמטר מופעל לחברה
  clientId?: number; // מזהה הלקוח שנוצר מהליד (משמש לזיהוי לידים שהפכו ללקוח)
  currentStatusName?: string;
  currentStatusColor?: string;
  currentStages?: ClientCurrentStageDto[];
}

// סיכום נתוני לקוח מינימליים עבור ליד שחתם (מסמכים, פגישות קרובות, ועדות שנסגרו)
export interface ClientSummaryAppointmentDto {
  status: number; // AppointmentStatus enum value
  statusName: string;
  appointmentDateTime?: string;
  doctorName: string;
}

export interface ClientSummaryCommitteeDto {
  committeeDate: string;
  decisionStatus: number; // CommitteeDecisionStatus enum value
  isClosed: boolean;
  decisionStatusName: string;
  decisionNotes?: string;
  disabilityPercentage?: number;
  monthlyBenefitAmount?: number;
}

export interface ClientSummaryForLeadDto {
  clientId: number;
  documentCount: number;
  appointments: ClientSummaryAppointmentDto[];
  committees: ClientSummaryCommitteeDto[];
}

export interface CreateLeadDto {
  fullName: string;
  email?: string;
  contacts: CreateContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  departmentId?: number; // שיוך-מחלקה ביצירה (כל הצוות; ברירת-מחדל מחלקת היוצר)
  notes?: string;
  benefitType: number; // סוג קצבה (חובה)
  otherBenefitDescription?: string; // פירוט סוג קצבה במקרה של "אחר"
  // P8b: שיוך סוכן/ארגון מפנה + עמלות פר-תיק (כל הצוות ממלא ביצירה; null נשמר)
  agentSourceId?: number | null;
  organizationSourceId?: number | null;
  agentSigningCommissionAmount?: number | null;
  agentApprovalCommissionPercentage?: number | null;
  organizationSigningCommissionAmount?: number | null;
  organizationApprovalCommissionPercentage?: number | null;
  // שדות משותפים
  clientAddress?: string;
  clientCity?: string;
  clientIdNumber?: string;
  clientBirthDate?: string;
  isClientForeignResident?: boolean;
  // טוגל סוג מוטב
  beneficiaryType?: string;
  // שדות הורים
  fatherName?: string;
  fatherIdNumber?: string;
  fatherAddress?: string;
  fatherCity?: string;
  fatherBirthDate?: string;
  isFatherForeignResident?: boolean;
  motherName?: string;
  motherIdNumber?: string;
  motherAddress?: string;
  motherCity?: string;
  motherBirthDate?: string;
  isMotherForeignResident?: boolean;
  // שדות בן/בת זוג
  spouseName?: string;
  spouseAddress?: string;
  spouseCity?: string;
  spouseIdNumber?: string;
  spouseBirthDate?: string;
  isSpouseForeignResident?: boolean;
  // מגיש הבקשה
  submitterSourceType?: string;
  submitterName?: string;
  submitterRelationship?: string;
  submitterIdNumber?: string;
  submitterAddress?: string;
  submitterCity?: string;
}

export interface UpdateLeadDto {
  fullName: string;
  email?: string;
  contacts: UpdateContactDto[]; // רשימת אנשי קשר (טלפון + אימייל אופציונלי)
  notes?: string;
  statusId: number;
  benefitType: number; // סוג קצבה (חובה)
  otherBenefitDescription?: string; // פירוט סוג קצבה במקרה של "אחר"
  // P8b: שיוך סוכן/ארגון מפנה + עמלות פר-תיק. השיוך עריך לכל הצוות; ערכי העמלה — רק CanViewClientFinance
  agentSourceId?: number | null;
  organizationSourceId?: number | null;
  agentSigningCommissionAmount?: number | null;
  agentApprovalCommissionPercentage?: number | null;
  organizationSigningCommissionAmount?: number | null;
  organizationApprovalCommissionPercentage?: number | null;
  // שדות משותפים
  clientAddress?: string;
  clientCity?: string;
  clientIdNumber?: string;
  clientBirthDate?: string;
  isClientForeignResident?: boolean;
  // טוגל סוג מוטב
  beneficiaryType?: string;
  // שדות הורים
  fatherName?: string;
  fatherIdNumber?: string;
  fatherAddress?: string;
  fatherCity?: string;
  fatherBirthDate?: string;
  isFatherForeignResident?: boolean;
  motherName?: string;
  motherIdNumber?: string;
  motherAddress?: string;
  motherCity?: string;
  motherBirthDate?: string;
  isMotherForeignResident?: boolean;
  // שדות בן/בת זוג
  spouseName?: string;
  spouseAddress?: string;
  spouseCity?: string;
  spouseIdNumber?: string;
  spouseBirthDate?: string;
  isSpouseForeignResident?: boolean;
  // מגיש הבקשה
  submitterSourceType?: string;
  submitterName?: string;
  submitterRelationship?: string;
  submitterIdNumber?: string;
  submitterAddress?: string;
  submitterCity?: string;
}

// עדכון חלקי (PATCH) — השרת מקבל כרגע הערות כלליות בלבד
export interface PartialUpdateLeadDto {
  notes?: string;
}

export interface LeadActivityDto {
  id: number;
  leadId: number;
  activityType: number;
  activityTypeName: string;
  description: string;
  performedByUserId: number;
  performedByUserName: string;
  createdAt: string;
  additionalData?: string;
}

export interface LeadFilterDto {
  page?: number;
  pageSize?: number;
  searchTerm?: string;
  statusIds?: number[];
  departmentIds?: number[]; // סינון לפי מחלקות (multi-select; -1 = ללא מחלקה)
  agentSourceId?: number; // P8b: סינון לפי סוכן (יחיד; -1 = ללא סוכן)
  organizationSourceId?: number; // P8b: סינון לפי ארגון (יחיד; -1 = ללא ארגון)
  dateFrom?: string;
  dateTo?: string;
}

export interface LeadPagedResultDto {
  leads: LeadDto[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface BulkDeleteDto {
  leadIds: number[];
}

// שיוך מחלקה המוני ללידים (Manager בלבד). departmentId undefined/null => ניקוי
export interface BulkLeadDepartmentDto {
  leadIds: number[];
  departmentId?: number | null;
}

export interface BulkDepartmentResultDto {
  success: boolean;
  updatedCount: number;
  errors: string[];
}

export interface ImportResultDto {
  successCount: number;
  errorCount: number;
  duplicateCount: number;
  errors: string[];
  duplicateLeads: LeadDto[];
  hasErrors: boolean;
}

export interface AddLeadNoteDto {
  note: string;
}

export interface UpdateLeadNoteDto {
  note: string;
}

// Leads API
export const leadsApi = {
  getAll: async (filter?: LeadFilterDto): Promise<LeadPagedResultDto> => {
    const params = new URLSearchParams();
    
    if (filter) {
      if (filter.page) params.append('page', filter.page.toString());
      if (filter.pageSize) params.append('pageSize', filter.pageSize.toString());
      if (filter.searchTerm) params.append('searchTerm', filter.searchTerm);
      if (filter.statusIds && filter.statusIds.length > 0) {
        filter.statusIds.forEach(id => params.append('statusIds', id.toString()));
      }
      if (filter.departmentIds && filter.departmentIds.length > 0) {
        filter.departmentIds.forEach(id => params.append('departmentIds', id.toString()));
      }
      if (filter.agentSourceId !== undefined) params.append('agentSourceId', filter.agentSourceId.toString());
      if (filter.organizationSourceId !== undefined) params.append('organizationSourceId', filter.organizationSourceId.toString());
      if (filter.dateFrom) params.append('dateFrom', filter.dateFrom);
      if (filter.dateTo) params.append('dateTo', filter.dateTo);
    }

    const response = await axios.get(`/leads?${params.toString()}`);
    return response.data;
  },

  getById: async (id: number): Promise<LeadDto> => {
    const response = await axios.get(`/leads/${id}`);
    return response.data;
  },

  // סיכום נתוני לקוח מינימליים עבור ליד שחתם (זמין לנציג מכירות כשהפרמטר מופעל לחברה)
  getClientSummary: async (leadId: number): Promise<ClientSummaryForLeadDto> => {
    const response = await axios.get(`/leads/${leadId}/client-summary`);
    return response.data;
  },

  create: async (data: CreateLeadDto): Promise<LeadDto> => {
    const response = await axios.post('/leads', data);
    return response.data;
  },

  update: async (id: number, data: UpdateLeadDto): Promise<LeadDto> => {
    const response = await axios.put(`/leads/${id}`, data);
    return response.data;
  },
  
  partialUpdate: async (id: number, data: PartialUpdateLeadDto): Promise<LeadDto> => {
    const response = await axios.patch(`/leads/${id}`, data);
    return response.data;
  },

  delete: async (id: number): Promise<void> => {
    await axios.delete(`/leads/${id}`);
  },

  checkDuplicate: async (phone: string): Promise<LeadDto | null> => {
    const response = await axios.get(`/leads/check-duplicate?phone=${encodeURIComponent(phone)}`);
    return response.data;
  },

  bulkDelete: async (data: BulkDeleteDto): Promise<number> => {
    const response = await axios.post('/leads/bulk-delete', data);
    return response.data;
  },

  // שיוך/ניקוי מחלקה לליד בודד (Manager בלבד). departmentId null => ניקוי
  setDepartment: async (id: number, departmentId: number | null): Promise<LeadDto> => {
    const response = await axios.patch(`/leads/${id}/department`, { departmentId });
    return response.data;
  },

  // שיוך מחלקה המוני ללידים (Manager בלבד)
  bulkAssignDepartment: async (data: BulkLeadDepartmentDto): Promise<BulkDepartmentResultDto> => {
    const response = await axios.post('/leads/bulk-department', data);
    return response.data;
  },

  getActivities: async (leadId: number): Promise<LeadActivityDto[]> => {
    const response = await axios.get(`/leads/${leadId}/activities`);
    return response.data;
  },

  import: async (csvContent: string): Promise<ImportResultDto> => {
    const response = await axios.post('/leads/import', JSON.stringify(csvContent), {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  },

  search: async (searchTerm: string, filters?: Omit<LeadFilterDto, 'searchTerm'>): Promise<LeadPagedResultDto> => {
    return leadsApi.getAll({ ...filters, searchTerm });
  },

  addNote: async (leadId: number, data: AddLeadNoteDto): Promise<LeadActivityDto> => {
    const response = await axios.post(`/leads/${leadId}/notes`, data);
    return response.data;
  },

  updateNote: async (leadId: number, noteId: number, data: UpdateLeadNoteDto): Promise<LeadActivityDto> => {
    const response = await axios.put(`/leads/${leadId}/notes/${noteId}`, data);
    return response.data;
  },

  deleteNote: async (leadId: number, noteId: number): Promise<void> => {
    await axios.delete(`/leads/${leadId}/notes/${noteId}`);
  }
};

// טיפול אחיד בשגיאות API — מקור אמת מרכזי
export { handleApiError } from '../utils/errorHandler';

// Helper function to format activity type names
export const getActivityTypeName = (activityType: number): string => {
  switch (activityType) {
    case 1: return 'נוצר';
    case 2: return 'הוקצה';
    case 3: return 'שונה סטטוס';
    case 4: return 'עודכן';
    case 5: return 'פנייה';
    case 6: return 'הערה';
    default: return 'לא ידוע';
  }
};

// Helper function to format dates
export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// Helper function to validate phone number
export const validatePhone = (phone: string): boolean => {
  const phoneRegex = /^[0-9\-+()\s]{9,15}$/;
  return phoneRegex.test(phone.trim());
};

// Helper function to validate email
export const validateEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

// Benefit Type Enum (matching backend)
export enum BenefitType {
  GeneralPhysicalDisability = 1, // נכות כללית פיזי
  GeneralMentalDisability = 2,   // נכות כללית נפשי
  DisabledChildPhysical = 3,     // ילד נכה פיזי
  DisabledChildMental = 4,       // ילד נכה נפשי
  ShrM = 5,                      // שר"מ
  NursingCare = 6,               // סיעוד
  WorkDisability = 7,            // נכות מעבודה
  Mobility = 8,                  // ניידות
  Other = 9                      // אחר
}

// Helper function to get benefit type name in Hebrew
export const getBenefitTypeName = (benefitType: number): string => {
  switch (benefitType) {
    case BenefitType.GeneralPhysicalDisability:
      return 'נכות כללית פיזי';
    case BenefitType.GeneralMentalDisability:
      return 'נכות כללית נפשי';
    case BenefitType.DisabledChildPhysical:
      return 'ילד נכה פיזי';
    case BenefitType.DisabledChildMental:
      return 'ילד נכה נפשי';
    case BenefitType.ShrM:
      return 'שר"מ';
    case BenefitType.NursingCare:
      return 'סיעוד';
    case BenefitType.WorkDisability:
      return 'נכות מעבודה';
    case BenefitType.Mobility:
      return 'ניידות';
    case BenefitType.Other:
      return 'אחר';
    default:
      return 'לא ידוע';
  }
};

// Helper function to get all benefit type options
export const getBenefitTypeOptions = (): { value: number; label: string }[] => {
  return [
    { value: BenefitType.GeneralPhysicalDisability, label: 'נכות כללית פיזי' },
    { value: BenefitType.GeneralMentalDisability, label: 'נכות כללית נפשי' },
    { value: BenefitType.DisabledChildPhysical, label: 'ילד נכה פיזי' },
    { value: BenefitType.DisabledChildMental, label: 'ילד נכה נפשי' },
    { value: BenefitType.ShrM, label: 'שר"מ' },
    { value: BenefitType.NursingCare, label: 'סיעוד' },
    { value: BenefitType.WorkDisability, label: 'נכות מעבודה' },
    { value: BenefitType.Mobility, label: 'ניידות' },
    { value: BenefitType.Other, label: 'אחר' }
  ];
};